import { ReactNode, Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Plus } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'

import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'

import { StudyReelFullscreen, TheaterReel } from './StudyReelFullscreen'

import { StudiesLobbyExperimentItem, StudiesLobbyItem } from '@/types/routes/tests'

/** The slider always holds this many slots: reels first, dotted slots after. */
const RAIL_SLOTS = 10

const SLOT_CLASS = 'w-[260px] max-w-full shrink-0 snap-start'

const SLOT_SHAPE_CLASS = 'aspect-[9/16] w-full rounded-xl'

const StudyReelEmbed = lazy(() =>
  import('./StudyReelEmbed').then((module) => ({ default: module.StudyReelEmbed })),
)

export interface RecentReelsRailProps {
  items: Array<StudiesLobbyItem>
  isLockedFree: boolean
  getStudyName: (item: StudiesLobbyItem) => string
  /** Card A, shown in a ready reel's slot only if the reel fails to load. */
  renderReel: (item: StudiesLobbyItem, index: number) => ReactNode
  onOpen: (item: StudiesLobbyItem) => void
}

function isRailExperiment(item: StudiesLobbyItem): item is StudiesLobbyExperimentItem {
  return item.itemKind === 'experiment'
}

/**
 * The studies the Recent rail shows: reels being built first (a just-finished
 * study stays in view), then ready reels, newest study first within each.
 * Everything else (no reel, a failed reel, drafts) lives in the table only.
 */
export function selectRailItems(items: Array<StudiesLobbyItem>): Array<StudiesLobbyExperimentItem> {
  const experiments = items.filter(isRailExperiment)
  const pending = experiments.filter((item) => item.reelStatus === 'pending')
  const ready = experiments.filter((item) => item.reelStatus === 'ready' && item.reelCard)
  return [...pending, ...ready].slice(0, RAIL_SLOTS)
}

/**
 * Whether the Recent rail shows at all. An org with studies but no reel yet
 * (every study finished before reels shipped) sees no rail rather than a
 * first-study panel; the rail appears with its first reel.
 */
export function shouldShowReelsRail(items: Array<StudiesLobbyItem>): boolean {
  if (selectRailItems(items).length > 0) {
    return true
  }
  return !items.some(isRailExperiment)
}

/**
 * The Recent rail when the org's `studyReel` flag is on. With reels: a slider
 * of ten slots, each ready reel playing in its own, the rest dotted. With none
 * yet: one panel that says where reels appear and starts a first study.
 */
export function RecentReelsRail(props: RecentReelsRailProps) {
  const { items, isLockedFree, getStudyName, renderReel, onOpen } = props

  const { t } = useTranslation(['app'])

  const railRef = useRef<HTMLDivElement>(null)
  const [canScrollBack, setCanScrollBack] = useState(false)
  const [canScrollForward, setCanScrollForward] = useState(false)
  const [theaterIndex, setTheaterIndex] = useState<number | null>(null)

  const railItems = selectRailItems(items)
  const placeholderCount = RAIL_SLOTS - railItems.length
  const isEmpty = railItems.length === 0
  const readyItems = railItems.filter((item) => item.reelStatus === 'ready')
  const theaterReels: Array<TheaterReel> = readyItems.map((item) => ({ token: item.id, studyName: getStudyName(item) }))

  const onOpenReport = (token: string) => {
    const item = readyItems.find((candidate) => candidate.id === token)
    if (item) {
      onOpen(item)
    }
  }

  const updateScrollState = useCallback(() => {
    const rail = railRef.current
    if (!rail) {
      return
    }
    setCanScrollBack(rail.scrollLeft > 1)
    setCanScrollForward(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const rail = railRef.current
    if (!rail) {
      return
    }
    updateScrollState()
    rail.addEventListener('scroll', updateScrollState, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateScrollState)
    observer?.observe(rail)
    return () => {
      rail.removeEventListener('scroll', updateScrollState)
      observer?.disconnect()
    }
  }, [updateScrollState, isEmpty])

  const onScrollBy = (direction: 1 | -1) => {
    const rail = railRef.current
    if (!rail) {
      return
    }
    rail.scrollBy({ left: direction * rail.clientWidth * 0.9, behavior: 'smooth' })
  }

  if (isEmpty) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-border bg-muted/20 px-6 py-12 text-center"
        data-testid="recent-reels-empty"
      >
        <div className="flex gap-2" aria-hidden>
          {[0, 1, 2].map((index) => (
            <span key={index} className="h-14 w-8 rounded-md border border-dashed border-border bg-background" />
          ))}
        </div>
        <Text className="font-sans text-sm text-muted-foreground">{t('studies.reelRail.empty')}</Text>
        {!isLockedFree && (
          <Button asChild variant="outline" size="sm">
            <Link href={ROUTES.testsNew} data-testid="recent-reels-new-study">
              <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} />
              {t('studies.createFirst')}
            </Link>
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <div className="absolute -top-10 right-0 flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label={t('studies.reelRail.previous')}
          disabled={!canScrollBack}
          onClick={() => onScrollBy(-1)}
          data-testid="recent-reels-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label={t('studies.reelRail.next')}
          disabled={!canScrollForward}
          onClick={() => onScrollBy(1)}
          data-testid="recent-reels-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div
        ref={railRef}
        className="relative z-10 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="recent-reels-rail"
      >
        {railItems.map((item, index) =>
          item.reelStatus === 'pending' ? (
            <div key={`reel:${item.id}`} className={SLOT_CLASS}>
              <button
                type="button"
                data-testid={`recent-reels-pending-${index}`}
                onClick={() => onOpen(item)}
                className={cn(
                  'flex flex-col justify-end gap-1 border border-border/70 bg-muted/40 p-5 text-left transition-colors hover:bg-muted/60',
                  SLOT_SHAPE_CLASS,
                )}
              >
                <span className="h-1 w-16 animate-pulse rounded-full bg-muted-foreground/20" />
                <Text className="mt-3 line-clamp-2 font-sans text-base font-medium text-foreground">
                  {getStudyName(item)}
                </Text>
                <Text className="font-sans text-sm text-muted-foreground">{t('studies.reelRail.pending')}</Text>
              </button>
            </div>
          ) : (
            <div key={`reel:${item.id}`} className={SLOT_CLASS} data-testid={`recent-reels-item-${index}`}>
              <Suspense fallback={<div className={cn('bg-muted/40', SLOT_SHAPE_CLASS)} />}>
                <StudyReelEmbed
                  token={item.id}
                  onOpenTheater={() => setTheaterIndex(readyItems.indexOf(item))}
                  onOpenReport={() => onOpen(item)}
                  fallback={renderReel(item, index)}
                />
              </Suspense>
            </div>
          ),
        )}
        {Array.from({ length: placeholderCount }, (_, placeholderIndex) => (
          <div key={`slot:${placeholderIndex}`} className={SLOT_CLASS} data-testid={`recent-reels-ghost-${placeholderIndex}`}>
            <div className={cn('border border-dashed border-border bg-muted/20', SLOT_SHAPE_CLASS)} />
          </div>
        ))}
      </div>
      <StudyReelFullscreen
        reels={theaterReels}
        index={theaterIndex}
        onIndexChange={setTheaterIndex}
        onOpenReport={onOpenReport}
      />
    </div>
  )
}
